import React from 'react';
import { Table, ConfigProvider } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { ArrowLeft, ArrowRight } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

type CustomTableProps<T> = TableProps<T> & {
  columns: ColumnsType<T>;
  dataSource: T[];
  currentPage: number;
  pageSize?: number;
  totalElements: number;
  onPageChange: (page: number, pageSize?: number) => void;
  onRowClick?: (e: React.MouseEvent<HTMLElement>, record: T) => void;
};

export default function CustomTable<T extends object>({
  columns,
  dataSource,
  currentPage,
  pageSize = 10,
  totalElements,
  onPageChange,
  onRowClick,
  scroll,
  className,
  ...props
}: CustomTableProps<T>) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const totalPages = Math.ceil(totalElements / pageSize);
  const safePage = Math.min(currentPage, totalPages || 1);

  const hasData = Boolean(dataSource && dataSource.length > 0);
  const resolvedScroll = hasData
    ? scroll
    : scroll?.y
    ? { y: scroll.y }
    : undefined;

  return (
    <ConfigProvider direction={language === 'ar' ? 'rtl' : 'ltr'}>
      <Table<T>
        className={classNames(
          'customTable',
          !hasData &&
            '[&_.ant-table-content]:!overflow-x-hidden [&_.ant-table-body]:!overflow-x-hidden',
          className
        )}
        columns={columns}
        dataSource={dataSource || []}
        scroll={resolvedScroll}
        {...props}
        pagination={
          props.pagination === false
            ? false
            : {
                current: safePage + 1,
                pageSize,
                total: totalElements,
                showSizeChanger: false,
                position: ['bottomCenter'],
                className: '!mt-8', // margin-top
                itemRender: (page, type, originalElement) => {
                  if (type === 'prev') {
                    return (
                      <a className={classNames('flex gap-3 px-2 items-center')}>
                        {language === 'ar' ? <ArrowRight /> : <ArrowLeft />}
                        {t('home.prev')}
                      </a>
                    );
                  }

                  if (type === 'next') {
                    return (
                      <a className={classNames('flex gap-3 px-2 items-center')}>
                        {t('home.next')}
                        {language === 'ar' ? <ArrowLeft /> : <ArrowRight />}
                      </a>
                    );
                  }

                  return originalElement;
                },
                onChange(page, pageSize) {
                  onPageChange(page, pageSize);
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  });
                },
              }
        }
        {...(!!onRowClick
          ? {
              onRow: (record) => ({
                className:
                  'hover:bg-gray-50 cursor-pointer transition duration-300 ease-in-out',
                onClick: (e: React.MouseEvent<HTMLElement>) => {
                  onRowClick(e, record);
                },
              }),
            }
          : {})}
      />
    </ConfigProvider>
  );
}
